// What a push can do with each audience - lib/api-integrations/actions on the
// server. Setting rates has no name changes; renaming has nothing about pricing.
export const ACTIONS = [
  {
    value: 'upsert',
    label: 'Upsert + set rates',
    hint:
      'Create the segments that are missing, and give the ones already there this name and rate.',
  },
  {
    value: 'set_rate',
    label: 'Set rates',
    hint:
      'Only the rate, on segments already there. Names stay as they are; missing segments are skipped.',
  },
  {
    value: 'rename',
    label: 'Rename',
    hint:
      'Only the name, on segments already there. Rates stay as they are; missing segments are skipped.',
  },
]

// An action in a partner's words, where they differ - Yahoo's upsert sets no
// rate, since its segments take their folder's. From the partner's file on
// the server (GET /api-integrations).
export const actionFor = (action, partner) => ({
  ...action,
  ...(partner?.action_labels?.[action.value] || {}),
})

// pushes saved before there was a choice did what upsert does
export const actionOf = (push, partner) =>
  actionFor(ACTIONS.find((a) => a.value === (push?.action || 'upsert')) || ACTIONS[0], partner)

// How each request to a partner that takes changes in batches (Yahoo) reads.
export const BATCH_STATES = {
  planned: { label: 'Not sent yet', className: 'text-gray-500 border-gray-400' },
  processing: { label: 'Processing', className: 'text-amber-700 border-amber-500 bg-amber-50' },
  accepted: { label: 'Accepted', className: 'text-green-700 border-green-600' },
  error: { label: 'Refused', className: 'text-red-600 border-red-500' },
  failed: { label: 'Not sent', className: 'text-red-600 border-red-500' },
  empty: { label: 'Nothing to send', className: 'text-gray-500 border-gray-400' },
}

// what Yahoo prices separately, as people say them
export const MEDIA_TYPE_LABELS = {
  display: 'Display',
  video: 'Video',
  connectedTv: 'Connected TV',
  audio: 'Audio',
  nativeDisplay: 'Native display',
  nativeVideo: 'Native video',
  doohDisplay: 'DOOH display',
  doohVideo: 'DOOH video',
}

// What a list of pushes reads - not their audience IDs or requests, which can
// run to hundreds of KB a push and are never on a list.
export const LIST_FIELDS = [
  '_id',
  'taxonomy',
  'partner',
  'mapping_id',
  'action',
  'status',
  'mocked',
  'created_at',
  'started_at',
  'heartbeat_at',
  'position',
  'counts',
  'waiting_for',
].join(',')

// "No taxonomy" in the form's taxonomy choice and the list's taxonomy filter.
// The API reads it as a push with none - a query string cannot say null.
export const NO_TAXONOMY = 'none'

// What a push with no taxonomy starts with. There is no taxonomy to send every
// audience of or pick from, so its audiences are listed by mapping ID -
// uploaded or pasted, and matched across every taxonomy.
export const NO_TAXONOMY_SELECTION = { mode: 'ids', audience_ids: [], source: 'csv' }

// How a push's status reads on screen, and whether it is still going.
export const STATUS_LABELS = {
  draft: 'Draft',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

export const STATUS_CLASSES = {
  draft: 'text-gray-500 border-gray-400',
  // green like the rest of the app, filled so it reads apart from Completed
  running: 'text-green-700 border-green-600 bg-green-50',
  completed: 'text-green-700 border-green-600',
  failed: 'text-red-600 border-red-500',
  cancelled: 'text-gray-500 border-gray-400',
}

// A push with no heartbeat for this long has stalled. Matches
// STALE_AFTER_MS in functions/api-integrations/sweepStalledPushes - keep the
// two in step, or the screen will offer Resume on a push the server still
// considers healthy.
export const STALE_AFTER_MS = 2 * 60 * 1000

// How often the status screen asks the server how it is getting on.
export const POLL_EVERY_MS = 5000

// What each name-change step asks the user for. Mirrors
// applyNameChanges.STEPS on the server, which is also returned by
// /preview-names if we ever want to drive this from the API instead.
export const NAME_STEPS = [
  { value: 'strip_prefix', label: 'Remove from the start', fields: ['text'] },
  { value: 'strip_suffix', label: 'Remove from the end', fields: ['text'] },
  { value: 'add_prefix', label: 'Add to the start', fields: ['text'] },
  { value: 'add_suffix', label: 'Add to the end', fields: ['text'] },
  { value: 'replace', label: 'Find and replace', fields: ['find', 'with'] },
  { value: 'keep_last_levels', label: 'Keep the last N parts', fields: ['count'] },
  { value: 'drop_first_levels', label: 'Drop the first N parts', fields: ['count'] },
  { value: 'max_length', label: 'Shorten to N characters', fields: ['length'] },
]

export const FIELD_LABELS = {
  text: 'Text',
  find: 'Find',
  with: 'Replace with',
  count: 'How many',
  length: 'Characters',
}

export const isStale = (push) => {
  if (!push || push.status !== 'running') return false
  const beat = push.heartbeat_at ? new Date(push.heartbeat_at).getTime() : 0
  return Date.now() - beat > STALE_AFTER_MS
}

export const sinceHeartbeat = (push) => {
  if (!push?.heartbeat_at) return null
  const seconds = Math.round((Date.now() - new Date(push.heartbeat_at).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  return `${Math.round(minutes / 60)}h ago`
}

// A pasted list of IDs. New lines, tabs, commas and spaces all separate them,
// so a column copied out of a spreadsheet, a comma-separated list and IDs on
// one line all work. Duplicates are left in - the server counts each ID once.
export const splitIds = (text) => (text || '').split(/[\s,]+/).filter(Boolean)
